import { useEffect, useRef } from 'react';

/**
 * Visibility-aware polling with error backoff.
 *
 * Why this exists: dozens of panels used to `setInterval(load, 15000)`.
 * Hidden tabs kept hammering the API (and a failing request retried in a
 * tight loop), which on production meant ~230 req/s of background noise.
 *
 * Behaviour:
 *   - never fires while `document.visibilityState !== 'visible'`;
 *   - runs once when the tab becomes visible again (throttled, so tab
 *     switching does not turn into a request storm);
 *   - on failure the delay doubles up to `intervalMs * maxBackoff`.
 *     A failure is a thrown error OR a `false` return value.
 *
 * `fn` identity may change on every render — it is read from a ref, so
 * the schedule is NOT restarted.
 */
export function useSmartPoll(fn, intervalMs, options = {}) {
  const { enabled = true, immediate = true, maxBackoff = 8 } = options;
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;

    let cancelled = false;
    let timer = null;
    let failures = 0;
    let lastRun = 0;

    const schedule = () => {
      if (cancelled) return;
      clearTimeout(timer);
      const delay = Math.min(intervalMs * 2 ** failures, intervalMs * maxBackoff);
      timer = setTimeout(run, delay);
    };

    const run = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        // Hidden tab: stay quiet, `visibilitychange` wakes us up.
        clearTimeout(timer);
        return;
      }
      lastRun = Date.now();
      try {
        const res = await fnRef.current();
        failures = res === false ? Math.min(failures + 1, 5) : 0;
      } catch (_) {
        failures = Math.min(failures + 1, 5);
      }
      schedule();
    };

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') { clearTimeout(timer); return; }
      const gap = Math.min(intervalMs, 30000);
      if (Date.now() - lastRun < gap) { schedule(); return; }
      run();
    };

    document.addEventListener('visibilitychange', onVisibility);
    if (immediate) run(); else schedule();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, intervalMs, immediate, maxBackoff]);
}

export default useSmartPoll;
